import PageLoader from '@/common/service/flow/page-loader';
import { buildAccountBaseService } from '@/common/service/base-paid';
import { loadSelectByPersist } from '@/common/service/plan-data-mapper';
import { pageConfig } from '@/common/service/page-config';
import _ from 'lodash';
import { isDebug } from '@/utils';
import store from '@/store/index';
import { flowTypes } from '@/common/service/page-config';
import {
  addToCartByCategoryAndBillingCycle,
  getCurrentPlanCycleDepFirstPlanForInit,
  planQueryParser,
} from '@/common/service/flow/utils';
import { addParamsIfApm, addParamsIfNeedUseVerification } from '@/common/service/checkout-utils';
import { pollingEventStatus } from '@/common/polling';
import { addTrialSuccessPage, wiseUpgradeSuccessPage } from '@/common/service/common';
import {
  configCapacityTypes,
  BILLING_CYCLE,
  DEFAULT_FREE_TRIAL_DAYS,
  BillingCycleEnum,
  VIEWS,
  planTypes,
} from '@/common/constants';
import Flow from '@/flows/flow';
import { getFreeTrialPlansByCampaignName, addFreeTrialPlans } from '@/apis';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { validAddonCycleForBase } from '@/common/service/validate/billing-cycle';
import { WISE } from '@/views/checkout/payment/helper/constant';
import { freeTrialErrorMessage } from '../BUY_TRIAL/index';
import qs from 'qs';

const DEFAULT_MONTHLY_FREE_TRIAL_PLANS = [
  planTypes.WEBINAR
]

const checkSelectedPlan = (planMapFlat = []) => {
  return planMapFlat.find((item) => item.cartInfo.selected_number > 0);
};

const filterFreeTrialPlansFromPlanMap = (freeTrialPlanList = [], campaign_code) => {
  return (planList = []) => {
    return planList.map((item) => {
      const plan = { ...item };
      // filter all plans with free trial in freeTrialPlanList
      const freeTrialPlan = freeTrialPlanList.find((free) => {
        return [plan.plan_code, plan?.priceChangeMappingPlanCode].includes(free.planCode);
      });
      if (freeTrialPlan) {
        plan.isOnlineFreeTrial = true;
        plan.trialDays = freeTrialPlan.trialDays || DEFAULT_FREE_TRIAL_DAYS;
        plan.trialCode = freeTrialPlan.trialCode || '';
        plan.campaignCode = campaign_code || '';
      }
      return plan;
    });
  };
};

const loadDefaultSelectByQuery = (boughtPlans = []) => {
  return (planList = []) => {
    const hasSelectedPlan = checkSelectedPlan(planList);
    if (hasSelectedPlan) return planList;
    let { from, from_sub, period, quantity = 1, editConfig } = pageConfig || {};
    const planType = editConfig?.planType;
    const freeTrialPlanList = planList.filter((p) => !!p.isOnlineFreeTrial); // filter free trial plan only
    // Notice: category codes from url not contains the capacity so it won't match the planList category, e.g. planQueryParser(from || from_sub) = eventsaascore, but `plan.category = eventsaascore100`
    const { category_codes: roughlyCategoryCodesWithoutCapacity } = planQueryParser(from || from_sub);
    const currentPlanCycleDepFirstPlan = getCurrentPlanCycleDepFirstPlanForInit(
      freeTrialPlanList,
      boughtPlans,
      planType,
    );
    // Set default values.
    // From PM: can we default to the lower billing model. For example if the user has annual default to monthly? Webinar is a expensive product I don't want to scare customers with high price point
    if(_.isEmpty(period)) {
      if (DEFAULT_MONTHLY_FREE_TRIAL_PLANS.includes(planType)) {
        period = BILLING_CYCLE.MONTHLY;
      } else {
        period = BILLING_CYCLE.ANNUAL;
      }
    }

    // compare depend base plan billing cycle with url query period(billing cycle) parameter
    if (currentPlanCycleDepFirstPlan && BillingCycleEnum[period] > currentPlanCycleDepFirstPlan?.cycleValue) {
      period = currentPlanCycleDepFirstPlan?.billing_cycle;
    }
    // add target plan to shopping cart
    roughlyCategoryCodesWithoutCapacity.forEach((category) => {
      addToCartByCategoryAndBillingCycle(
        freeTrialPlanList,
        getPlanTypeByCategory(category),
        category,
        period,
        quantity,
      );
    });
    return planList;
  };
};

const checkHasFreeTrial = (freeTrialPlans) => {
  return (planList) => {
    const ftPlan = freeTrialPlans.find((item) => item.type === pageConfig.editConfig.planType);
    if (ftPlan) {
      // TODO: need redirect trial2paid flow if needed
      location.replace('/billing');
    }
    return planList;
  };
};

const initShoppingCart = async (state) => {
  const { persist } = state;
  const editPlanType = pageConfig.editConfig.planType;
  const isCapacityPlan = _.includes(configCapacityTypes, editPlanType);
  let { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService({});
  const { campaign_code = '' } = pageConfig || {};
  const { result: freeTrialPlanList = [], errorMessage = '' } = await getFreeTrialPlansByCampaignName(campaign_code);
  // Just warning, won't block page loading.
  if (errorMessage) {
    freeTrialErrorMessage(errorMessage, flowTypes.add2trial);
  }

  const loader = new PageLoader(planMap);
  loader.decorate('filterFreeTrialPlansFromPlanMap', filterFreeTrialPlansFromPlanMap(freeTrialPlanList, campaign_code));
  !isCapacityPlan && loader.decorate('checkHasFreeTrial', checkHasFreeTrial(freeTrialPlans));
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQuery(boughtPlans));
  const planList = await loader.dispatch();
  return {
    allPlanMap,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  };
};

const checkOutSubmit = async (submitParams) => {
  const { campaign_code: campaignCode = '' } = pageConfig || {};
  const getters = store.getters;
  const { apmInfo } = getters;
  const payload = store.getters.orderDataPayload;
  const freeTrialPlan = payload?.planList?.find((plan) => !!plan.trialCode);
  const preview = submitParams?.preview ?? false;
  const previewActions = submitParams?.previewActions ?? [];
  let params = {
    orderData: payload,
    extension: {
      campaignCode,
      trialCode: freeTrialPlan?.trialCode || '',
      freeTrialSource: qs.parse(location.search.slice(1))['itm_source'] || 'Direct Link',
    },
    preview,
    previewActions,
  };

  isDebug && console.log('add trial submit params: ', params);

  addParamsIfNeedUseVerification(params.extension);
  addParamsIfApm(params);
  const result = await addFreeTrialPlans(params);
  const options = preview ? { previewKey: result?.result?.previewKey, rps: true } : { rps: true };
  const eventResult = await pollingEventStatus(options);
  if (!preview) {
    apmInfo.apmPaymentMethodName === WISE ? wiseUpgradeSuccessPage({}) : addTrialSuccessPage();
  }

  return {
    eventResult,
  };
};

const resolveView = () => {
  return VIEWS.COMPOUND;
};

const preCheck = async () => {
  // FREE TRIAL - check billing cycle same with base plan for future auto upgrade
  await validAddonCycleForBase();
};

export default {
  [flowTypes.add2trial]: class extends Flow {
    view() {
      return resolveView();
    }
    init() {
      return initShoppingCart(...arguments);
    }
    beforeSubmit() {
      return preCheck();
    }
    submit(submitParams) {
      return checkOutSubmit(submitParams);
    }
  },
};
